import { defineFeature } from '../define-feature';
import { TEST_IDS, sidebarNavTestId, sidebarSubTestId } from '../test-ids';

export default defineFeature({
  id: 'marketing.campaign.email.compose',
  category: 'marketing',
  title: "Générer le contenu d'une campagne email",
  intent:
    "Rédiger le corps d'une campagne email à partir d'un texte généré par Pulse — l'IA fournit le message et un bouton d'action optionnel, le serveur l'assemble automatiquement dans la mise en page de l'email, et vous peaufinez ensuite le visuel dans l'éditeur.",
  context: {
    why: "Rédiger le contenu d'une campagne email est souvent l'étape qui bloque les pros — écrire un texte à la fois vendeur et fidèle à sa voix demande du temps, de l'inspiration et un certain savoir-faire commercial. Pulse est là pour lever cette friction : à partir d'un brief court (l'occasion de la campagne, l'offre proposée, le ton attendu), il génère une première version de texte que vous n'avez plus qu'à relire et à ajuster. C'est particulièrement précieux quand vous avez plusieurs campagnes à préparer dans le mois ou que le blocage face à la page blanche vous fait repousser vos envois. Un point important à comprendre sur la manière dont ça fonctionne : Pulse ne produit pas la mise en page complète de l'email (avec ses images, son en-tête, son pied de page). Il fournit uniquement le texte du message, que le serveur insère automatiquement dans le bloc texte du gabarit par défaut. Cet assemblage automatique vous garantit un rendu propre et cohérent avec votre identité, sans que vous ayez à toucher au design. Vous pouvez ensuite peaufiner tout ce qui est visuel dans l'éditeur email — ajouter une image, changer les couleurs, réorganiser les blocs.",
    use_cases: [
      'Vous voulez lancer une campagne rapidement sans passer des heures sur la rédaction : Pulse vous fournit une base solide en quelques secondes.',
      "Vous manquez d'inspiration pour formuler votre offre : le brief à Pulse vous force à structurer l'offre puis vous livre une première rédaction.",
      'Vous voulez tester plusieurs angles pour la même campagne : générez plusieurs versions du texte via Pulse pour comparer.',
      'Vous préparez plusieurs campagnes similaires (Noël, Nouvel An, Épiphanie) : Pulse maintient une voix cohérente entre les envois grâce à vos réglages de personnalisation.',
      'Vous avez peu de temps entre deux clients pour finaliser une campagne : Pulse abrège drastiquement le temps de rédaction.',
    ],
  },
  required_scopes: ['campaign.email.update'],
  prerequisites: [
    {
      ref: 'marketing.campaign.email.create',
      why: 'La campagne doit déjà exister en brouillon pour recevoir son contenu.',
    },
  ],
  ui_entry_points: [{ route: '/marketing/campaigns', element: "Éditeur d'email de la campagne (bloc texte)" }],
  api: {
    primary_operation_id: 'ProCampaignEmailAppController_composeEmailContent',
    related_operation_ids: [
      'FieldAssistAppController_fieldAction',
      'ProCampaignEmailAppController_getProCampaignEmail',
    ],
  },
  steps: [
    "Générer le texte du message avec l'IA (outil de rédaction, type de champ « corps d'email »), à partir du brief du pro (occasion, offre, ton).",
    "Fournir ce texte (et, si pertinent, le libellé / l'URL du bouton d'action).",
    'Valider — le contenu est posé dans la mise en page par défaut, prêt à être relu et programmé.',
  ],
  gotchas: [
    "Pulse ne produit PAS le blob du builder : il fournit du **texte** (`body_html`, HTML léger avec `<br>`), le serveur l'insère dans le bloc texte du gabarit par défaut (en-tête logo, texte, bouton, pied de page légal).",
    "Rédiger le texte via l'outil de rédaction (`fieldAction`, kind `email_body`, action `generate`) AVANT d'appeler cet outil — ne pas inventer le texte sans passer par la génération IA partagée (cohérence de la voix de marque).",
    "Le bouton d'action pointe par défaut vers le lien de réservation du pro ; ne le surcharger que si le pro veut une autre destination.",
    'Cet outil écrase le contenu existant de la campagne : à utiliser sur un brouillon, pas pour patcher finement un email déjà composé dans le builder.',
  ],
  related_features: ['marketing.campaign.email.create', 'marketing.campaign.email.schedule'],
  demo_script: {
    duration_target_s: 25,
    steps: [
      {
        action: 'navigate',
        value: '/fr-FR',
        caption: 'Écrire une campagne qui vende bloque souvent : laissons Pulse rédiger le texte à votre place.',
      },
      {
        action: 'click',
        target: sidebarNavTestId('/marketing'),
        caption: 'Dans le menu latéral, ouvrez « Communication ».',
      },
      {
        action: 'click',
        target: sidebarSubTestId('/marketing/campaigns'),
        caption: 'Puis « Campagne marketing ».',
      },
      {
        action: 'navigate',
        value: '/fr-FR/marketing/campaigns/new/edit',
        caption: 'Ouvrez l’éditeur d’e-mail de votre campagne.',
      },
      {
        action: 'wait_for',
        target: TEST_IDS.email_builder,
        caption: 'L’éditeur de contenu s’affiche.',
      },
      {
        action: 'click',
        target: TEST_IDS.email_template_picker,
        caption: 'Partez d’un modèle, puis laissez Pulse rédiger le texte : vous n’avez plus qu’à relire et ajuster.',
        capture: true,
      },
    ],
  },
});
